import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import type { TaskDetail } from '@witty-board/shared'
import { Modal } from '../../components/Modal.js'
import { useDeleteTaskMutation } from '../../api/tasks.js'

interface DeleteTaskButtonProps {
  task: TaskDetail
}

/** Confirmación explícita; avisa de la cascada cuando tiene hijas (CLAUDE.md §4). */
export function DeleteTaskButton({ task }: DeleteTaskButtonProps) {
  const { t } = useTranslation()
  const { key } = useParams<{ key: string }>()
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const deleteTask = useDeleteTaskMutation()
  const hasChildren = task.children.length > 0

  function confirm(): void {
    deleteTask.mutate(
      { id: task.id, cascade: hasChildren },
      { onSuccess: () => void navigate(`/projects/${key}`) },
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="flex items-center gap-2 rounded-card px-2 py-1 text-meta text-danger hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <Trash2 className="h-3 w-3" aria-hidden="true" />
        {t('task.detail.deleteButton')}
      </button>

      {confirming && (
        <Modal titleId="delete-task-title" onClose={() => setConfirming(false)}>
          <div className="flex flex-col gap-4">
            <h2 id="delete-task-title" className="text-title font-semibold text-text">
              {t('task.detail.deleteConfirmTitle')}
            </h2>
            <p className="text-body text-text-muted">
              {hasChildren
                ? t('task.detail.deleteConfirmCascade', { count: task.children.length })
                : t('task.detail.deleteConfirmSimple')}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-card px-4 py-2 text-body text-text hover:bg-surface-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={deleteTask.isPending}
                className="rounded-card bg-danger px-4 py-2 text-body font-semibold text-text hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
